import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import MoreContentRequester from 'components/MoreContentRequester';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import { importedPatientsIndex } from 'services/api/importedPatients';
import { serializeSort, useCoordinatePatientsStore } from 'stores/patientFiltersStore';

import { CoordinateContentProps } from './contentProps';
import CoordinateDesktopContent from './CoordinateDesktopContent';
import CoordinateMobileContent from './CoordinateMobileContent';

function CoordinatePage() {
  const navigate = useNavigate();
  const [resultString, setResultString] = useState('');

  const sort = useCoordinatePatientsStore((state) => state.sort);
  const filters = useCoordinatePatientsStore((state) => state.filters);
  const search = useCoordinatePatientsStore((state) => state.search);

  const handleAddToPortfolio = useCallback((id: string) => navigate(`/coordinate/${id}`), [navigate]);

  const queryParams = useMemo(() => {
    return {
      pageSize: 10,
      sortBy: serializeSort(sort),
      search,
      filters,
      include: 'owner,transferred_from',
    };
  }, [search, filters, sort]);

  const patientsQuery = useInfiniteQuery({
    queryKey: ['importedPatients', queryParams],
    queryFn: ({ pageParam, signal }) => importedPatientsIndex({ ...queryParams, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: !!search,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
  });

  const patients = useMemo(() => {
    return patientsQuery.data?.pages.flatMap((page) => page.data);
  }, [patientsQuery.data]);

  useEffect(() => {
    if (patientsQuery.isPlaceholderData) return;

    const totalPatients = patientsQuery.data?.pages[0]?.meta.totalRecords ?? 0;

    if (totalPatients) {
      setResultString(`${totalPatients} ${totalPatients === 1 ? 'Result' : 'Results'} Found for '${search}'`);
    } else {
      setResultString(`0 Results found for '${search}'`);
    }
  }, [patientsQuery.isPlaceholderData, search, patientsQuery.data]);

  const props: CoordinateContentProps = useMemo(() => {
    return {
      patients,
      resultString,
      isLoading: patientsQuery.isFetching,
      onAddToPortfolio: handleAddToPortfolio,
      contentRequester: <MoreContentRequester resource={patientsQuery} />,
    };
  }, [patients, resultString, patientsQuery, handleAddToPortfolio]);

  return (
    <Page>
      <Page.Header title='Coordinate Patients' />
      <MobileOnly>
        <CoordinateMobileContent {...props} />
      </MobileOnly>
      <DesktopOnly>
        <CoordinateDesktopContent {...props} />
      </DesktopOnly>
    </Page>
  );
}

export default CoordinatePage;
